import { Reveal, Eyebrow } from "./Reveal";
import { motion } from "framer-motion";
import { Award, HeartHandshake, ShieldCheck, ArrowRight } from "lucide-react";

const COACH_PHOTO =
  "https://customer-assets-0z36b82j.emergentagent.net/job_premium-cse-offer/artifacts/f1ldjqrn_Photo%20Eddy%20%26%20Claudette%202025.jpeg";

const COACHES = [
  { name: "Claudette", role: "Coach Bien-être & Nutrition" },
  { name: "Eddy", role: "Coach Sportif & BodyFitChallenge" },
];

const CREDENTIALS = [
  { icon: Award, text: "17 ans d'expérience en coaching bien-être et sportif aux Antilles" },
  { icon: HeartHandshake, text: "Une approche sur-mesure, bienveillante et sans jugement, pour tous les niveaux" },
  { icon: ShieldCheck, text: "Analyses corporelles confidentielles et suivi personnalisé sur 21 jours" },
];

export const Coaches = () => {
  return (
    <section id="expertise" className="relative bg-sand-warm py-24 sm:py-32 overflow-hidden" data-testid="coaches-section">
      <div className="absolute -top-10 -left-10 w-80 h-80 rounded-full bg-palm-light/60 blur-[100px] pointer-events-none" />

      <div className="relative max-w-6xl mx-auto px-6 sm:px-8 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        {/* Photo — premium clipped frame */}
        <Reveal>
          <div className="relative max-w-md mx-auto lg:mx-0">
            <div className="absolute -inset-3 rounded-[2.25rem] bg-gradient-to-br from-gold/40 to-ocean/20 -z-0" />
            <div className="absolute -bottom-5 -right-5 w-32 h-32 rounded-full bg-ocean hidden sm:block -z-0" />
            <motion.div
              initial={{ scale: 1.06 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
              className="relative rounded-[2rem] overflow-hidden ring-1 ring-gold/40 shadow-[0_30px_70px_rgba(10,34,40,0.25)]"
            >
              <div className="aspect-[3/4]">
                <img
                  src={COACH_PHOTO}
                  alt="Claudette et Eddy, vos coachs bien-être"
                  className="w-full h-full object-cover"
                  style={{ objectPosition: "center 20%" }}
                  data-testid="coaches-photo"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-ocean-dark/45 via-transparent to-transparent pointer-events-none" />
              <div className="absolute bottom-4 left-4 right-4 flex items-center gap-3">
                {COACHES.map((c) => (
                  <div key={c.name} className="flex-1 rounded-2xl bg-white/15 backdrop-blur-md border border-white/25 px-3 py-2">
                    <div className="text-white font-serif text-lg leading-none">{c.name}</div>
                    <div className="text-white/75 text-[10px] mt-1 leading-tight">{c.role}</div>
                  </div>
                ))}
              </div>
            </motion.div>
            <div className="absolute -top-4 -left-4 rounded-full bg-gold text-ocean-dark text-[11px] font-bold uppercase tracking-[0.15em] px-4 py-2 shadow-lg" data-testid="coaches-badge">
              Vos coachs
            </div>
          </div>
        </Reveal>

        {/* Copy */}
        <div>
          <Reveal><Eyebrow>L'humain avant tout</Eyebrow></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-4 font-serif font-semibold tracking-tight text-ocean text-3xl sm:text-4xl lg:text-5xl leading-[1.08]">
              L'expertise derrière votre Journée Bien-être
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-5 text-base sm:text-lg text-muted2 leading-relaxed">
              Derrière chaque journée, un duo passionné : <strong className="text-ocean">Claudette & Eddy</strong>.
              Ensemble, ils allient énergie sportive, pédagogie nutritionnelle et véritable art du lâcher-prise
              pour offrir à vos équipes une expérience aussi conviviale qu'exigeante.
            </p>
          </Reveal>

          <div className="mt-8 space-y-4">
            {CREDENTIALS.map((c, i) => (
              <Reveal key={i} delay={0.15 + i * 0.08}>
                <div className="flex gap-4 items-start" data-testid={`coach-credential-${i}`}>
                  <span className="flex-shrink-0 w-11 h-11 rounded-2xl bg-white border border-gold/30 flex items-center justify-center">
                    <c.icon className="w-5 h-5 text-gold-soft" strokeWidth={1.8} />
                  </span>
                  <p className="text-sm sm:text-base text-ink leading-relaxed pt-1.5">{c.text}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.4}>
            <button
              onClick={() => document.querySelector("#formulaire-devis")?.scrollIntoView({ behavior: "smooth" })}
              className="mt-9 inline-flex items-center gap-2 rounded-full bg-ocean hover:bg-ocean-dark text-white font-semibold px-7 py-4 text-base transition-all hover:-translate-y-0.5"
              data-testid="coaches-cta-button"
            >
              Rencontrer nos coachs le jour J
              <ArrowRight className="w-5 h-5" />
            </button>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
